import { getSupabaseClient } from '../supabase-client';
import { readSupabaseEnv } from '../../lib/env';
import { reportError } from '../../lib/monitoring';

/**
 * Asking Crewlink to read one authorised chunk aloud. M9 / S09-04, Pass B.
 *
 * **ONE AUTHENTICATED CALL TO CREWLINK'S OWN ENDPOINT, AND NOTHING ELSE.** No
 * provider is named here, no model is chosen here, no voice is chosen here, no
 * speaking style exists here, and no key is reachable from here (D-012). The
 * vendor is deliberately unnamed even in a comment, so `check:secrets:build` can
 * assert the strong property: the name appears in no client source and in no
 * built artifact.
 *
 * **THE BROWSER SENDS BACK THE SERVER'S OWN WORDS.** `chunkText` is a string
 * `sol-ask` produced and handed over with the ticket; the server re-hashes it
 * and refuses anything else. **The client never composes speakable text**, which
 * is the whole reason this endpoint is not a free text-to-speech service billed
 * to Jerry.
 *
 * **NO AUTOMATIC RETRY.** Speech costs money per call, and the text answer is
 * already on the screen and is authoritative. A retry is Brandon's tap.
 */

export const SOL_SPEAK_FUNCTION_NAME = 'sol-speak';

export type SolSpeechOutcome =
  | { readonly status: 'spoken'; readonly bytes: ArrayBuffer; readonly mimeType: string }
  /**
   * **EVERY REFUSAL LOOKS THE SAME, BY SERVER DESIGN.** Expired, tampered,
   * another user's, switched off, unconfigured — the endpoint answers all of
   * them identically so nobody can probe it, and the client has nothing to
   * distinguish either. All of them mean the same thing to Brandon.
   */
  | { readonly status: 'unavailable' }
  | { readonly status: 'aborted' };

export interface SolSpeechRequest {
  readonly ticket: string;
  readonly chunkIndex: number;
  /** The server's own canonical chunk, verbatim. */
  readonly chunkText: string;
  readonly signal?: AbortSignal;
}

function isAbort(caught: unknown): boolean {
  return caught instanceof Error && caught.name === 'AbortError';
}

export async function speakSolChunk(request: SolSpeechRequest): Promise<SolSpeechOutcome> {
  const env = readSupabaseEnv();
  const client = getSupabaseClient();
  if (!env || !client) return { status: 'unavailable' };

  try {
    const { data, error } = await client.auth.getSession();
    if (error) return { status: 'unavailable' };
    const accessToken = data.session?.access_token;
    if (!accessToken) return { status: 'unavailable' };

    const response = await fetch(
      `${env.url.replace(/\/+$/, '')}/functions/v1/${SOL_SPEAK_FUNCTION_NAME}`,
      {
        method: 'POST',
        headers: {
          apikey: env.anonKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        // Three fields. **The endpoint REFUSES any other rather than ignoring
        // it**, which is what makes "the browser cannot choose a voice" a
        // property of the contract rather than of this file's good manners.
        body: JSON.stringify({
          ticket: request.ticket,
          chunkIndex: request.chunkIndex,
          chunkText: request.chunkText,
        }),
        ...(request.signal ? { signal: request.signal } : {}),
      },
    );

    if (!response.ok) return { status: 'unavailable' };

    // **THE CONTENT TYPE IS CHECKED BEFORE A BLOB IS BUILT.** A JSON error body
    // served with a 200 would otherwise become an object URL that plays as
    // silence — a failure indistinguishable from a quiet phone.
    const mimeType = response.headers.get('Content-Type') ?? '';
    if (!mimeType.toLowerCase().startsWith('audio/')) return { status: 'unavailable' };

    const bytes = await response.arrayBuffer();
    if (bytes.byteLength === 0) return { status: 'unavailable' };

    return { status: 'spoken', bytes, mimeType };
  } catch (caught) {
    if (isAbort(caught)) return { status: 'aborted' };
    // **NO CONTENT IS REPORTED.** The tag names the operation; the chunk text,
    // the ticket and the audio never leave the device through this path.
    reportError(caught, 'sol.speak');
    return { status: 'unavailable' };
  }
}
